import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineInformationCircle,
  HiOutlineSparkles,
  HiOutlineExclamationTriangle,
  HiOutlineCreditCard,
  HiOutlineXMark,
} from "react-icons/hi2";
import { Modal, Tooltip } from "antd";
import { billingApi, UpcomingInvoice, usersApi } from "../../../api";
import {
  APP_ROUTES,
  IPlan,
  IResponseError,
  IUser,
} from "../../../data-access";
import { FormButton } from "../../../ui";
import { useUser } from "../../../hooks";
import { getTrialPlanDayTillExpired } from "../../../util";
import { getDisplayPlanName } from "../../../util/plan-gates";
import { RootState } from "../../../store";
import { storeBillingActions } from "../../../store/slices/billing";
import { storeAuthActions } from "../../../store/slices/auth";
import { handleFailedRequest, notify } from "../../../util";
import { PlanCard } from "./plan-card";
import { ByokKeyAlert, ByokKeyModal } from "../../byok-key";
import "./current-plan.scss";

export const CurrentPlan = (): JSX.Element => {
  const { currentUser } = useUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentPlan = useSelector(
    (state: RootState) => state.billing.currentPlan
  );
  const [isFetching, setIsFetching] = useState<boolean>(!currentPlan);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);

  useEffect(() => {
    setIsFetching(true);

    // Refresh user data (e.g. after returning from Checkout)
    usersApi.getMe().then((response: { data: IUser }) => {
      dispatch(storeAuthActions.setUserData({ user: response.data }));
    });

    billingApi
      .getPlans()
      .then((response: { data: IPlan[] }) => {
        const currentPlan = response.data.find(
          (plan: IPlan) => plan.is_selected
        );
        dispatch(
          storeBillingActions.update({
            plans: response.data,
            currentPlan,
          })
        );
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsFetching(false));
  }, [dispatch]);

  const showHowChangePlan = (): void => {
    navigate(APP_ROUTES.plans);
  };

  const handleCancelSubscription = (): void => {
    setIsCanceling(true);
    billingApi
      .cancelSubscription()
      .then((response) => {
        const cancelDate = response.cancel_at
          ? new Date(response.cancel_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
          : null;
        notify("success", `Subscription will be canceled${cancelDate ? ` on ${cancelDate}` : " at the end of the billing period"}.`);
        setShowCancelModal(false);
        // Refresh user data and upcoming invoice
        usersApi.getMe().then((resp: { data: IUser }) => {
          dispatch(storeAuthActions.setUserData({ user: resp.data }));
        });
        billingApi.getUpcomingInvoice()
          .then((data) => setUpcomingInvoice(data))
          .catch(() => {});
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsCanceling(false));
  };

  const aiCreditsTotal = currentUser?.ai_credits_total || 0;
  const aiCreditsUsed = currentUser?.ai_credits_used || 0;
  const aiCreditsRemaining = aiCreditsTotal - aiCreditsUsed;

  const isTrial = currentUser?.billing_status === "trial";

  const getCreditsSubtitle = (): string | undefined => {
    if (isTrial) {
      // Trial is a single fixed budget for the whole trial period (no daily
      // reset). Show only the trial expiration date so the user knows when
      // the trial ends.
      const expiresAt = currentUser?.trial_expires_at;
      if (!expiresAt) return undefined;
      const date = new Date(expiresAt);
      const formatted = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return `Trial ends ${formatted}`;
    }
    const resetAt = currentUser?.credits_reset_at;
    if (!resetAt) return undefined;
    const date = new Date(resetAt);
    const formatted = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `Resets ${formatted}`;
  };
  const billingStatus = currentUser?.billing_status;
  const hasActiveSubscription = currentUser?.has_active_subscription;
  const isPastDue = billingStatus === "past_due";
  const isCanceled = billingStatus === "canceled";
  const showSubscribeCta =
    !hasActiveSubscription && !isPastDue && !isCanceled && (isTrial || billingStatus === "expired");

  const [upcomingInvoice, setUpcomingInvoice] = useState<UpcomingInvoice | null>(null);

  useEffect(() => {
    if (hasActiveSubscription) {
      billingApi.getUpcomingInvoice()
        .then((data) => setUpcomingInvoice(data))
        .catch(() => {});
    }
  }, [hasActiveSubscription]);

  const pendingPlan = upcomingInvoice?.pending_plan;

  const isCancelingSubscription = upcomingInvoice?.canceling === true;

  const getUpcomingLabel = (): string | undefined => {
    if (!upcomingInvoice) return undefined;

    if (isCancelingSubscription) {
      const cancelDate = upcomingInvoice.cancel_at
        ? new Date(upcomingInvoice.cancel_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
        : null;
      return `Cancels${cancelDate ? ` on ${cancelDate}` : " at end of billing period"}`;
    }

    const intLabel = upcomingInvoice.interval === "yearly" ? "/yr" : "/mo";
    const date = upcomingInvoice.next_payment_date
      ? new Date(upcomingInvoice.next_payment_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : null;
    const displayAmount = upcomingInvoice.tax > 0 ? upcomingInvoice.total : upcomingInvoice.amount;
    const taxPart = upcomingInvoice.tax > 0 ? " incl. tax" : "";

    if (pendingPlan) {
      return `Switching to ${pendingPlan.name} · $${displayAmount.toFixed(2)}${taxPart}${intLabel}${date ? ` from ${date}` : ""}`;
    }

    return `$${displayAmount.toFixed(2)}${taxPart}${intLabel}${date ? ` · Renews ${date}` : ""}`;
  };

  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  const handleUpdatePayment = (): void => {
    setIsLoadingPortal(true);
    billingApi
      .getCustomerPortal()
      .then((response) => {
        window.location.href = response.url;
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsLoadingPortal(false));
  };

  return (
    <div className="subscription-overview">
      <ByokKeyAlert />
      {isPastDue && (
        <div className="subscribe-cta subscribe-cta--past-due">
          <div className="subscribe-cta__icon">
            <HiOutlineCreditCard size={20} />
          </div>
          <div className="subscribe-cta__content">
            <p className="subscribe-cta__title">Payment Failed</p>
            <p className="subscribe-cta__text">
              Your last payment didn't go through. Update your payment method to keep your subscription active.
            </p>
          </div>
          <FormButton
            theme="green-light"
            className="subscribe-cta__button"
            loading={isLoadingPortal}
            disabled={isLoadingPortal}
            onClick={handleUpdatePayment}
          >
            Update Payment
          </FormButton>
        </div>
      )}
      {isCanceled && (
        <div className="subscribe-cta subscribe-cta--expired">
          <div className="subscribe-cta__icon">
            <HiOutlineExclamationTriangle size={20} />
          </div>
          <div className="subscribe-cta__content">
            <p className="subscribe-cta__title">Subscription Canceled</p>
            <p className="subscribe-cta__text">
              Your subscription has been canceled. Subscribe again to restore access to all features.
            </p>
          </div>
          <FormButton
            theme="green-light"
            className="subscribe-cta__button"
            onClick={showHowChangePlan}
          >
            View Plans
          </FormButton>
        </div>
      )}
      {showSubscribeCta && (
        <div className={`subscribe-cta${!isTrial ? " subscribe-cta--expired" : ""}`}>
          <div className="subscribe-cta__icon">
            {isTrial ? <HiOutlineSparkles size={20} /> : <HiOutlineExclamationTriangle size={20} />}
          </div>
          <div className="subscribe-cta__content">
            <p className="subscribe-cta__title">
              {isTrial
                ? `Trial Active - ${getTrialPlanDayTillExpired(String(currentUser?.trial_expires_at))} days left`
                : "Trial Ended"}
            </p>
            <p className="subscribe-cta__text">
              {isTrial
                ? "Choose a plan to unlock full access and keep your data."
                : "Subscribe now to restore access to all features."}
            </p>
          </div>
          <FormButton
            theme="green-light"
            className="subscribe-cta__button"
            onClick={showHowChangePlan}
          >
            View Plans
          </FormButton>
        </div>
      )}
      <div className={`plan-cards${currentUser?.byok?.enabled ? " plan-cards--four" : ""}`}>
        <PlanCard
          label="Plan"
          value={
            <>
              {/* The plans list only carries public plans (+ the team's plan
                  while a subscription is active), so a trial team's plan isn't
                  in it - /me still knows it. */}
              {getDisplayPlanName(currentPlan?.name || currentUser?.plan, currentUser?.billing_status) || "None"}
              {isTrial && (
                <div className="trial-badge">Trial</div>
              )}
              {isPastDue && (
                <div className="past-due-badge">Past Due</div>
              )}
              {isCancelingSubscription && (
                <div className="canceling-badge">Canceling</div>
              )}
            </>
          }
          subtitle={hasActiveSubscription ? getUpcomingLabel() : undefined}
          buttonText={isPastDue ? "Update Payment" : isCancelingSubscription ? "View Plans" : hasActiveSubscription ? "Switch" : "Subscribe"}
          isLoading={isFetching}
          onClick={isPastDue ? handleUpdatePayment : showHowChangePlan}
          disabled={isLoadingPortal}
        />
        <PlanCard
          label="Seats"
          value={`${currentUser?.teammates || 1} seats`}
          buttonText="Manage"
          isLoading={isFetching}
          onClick={() => navigate(APP_ROUTES.team)}
        />
        <PlanCard
          label="Credits"
          value={`${aiCreditsRemaining} / ${aiCreditsTotal}`}
          subtitle={getCreditsSubtitle()}
          buttonText={hasActiveSubscription ? "Top Up" : undefined}
          isLoading={isFetching}
          onClick={() => navigate(APP_ROUTES.aicredits + "?topup=1")}
        />
        {/* Own OpenRouter key (Flex, paid only - backend sends byok.enabled) */}
        {currentUser?.byok?.enabled && (() => {
          const byok = currentUser.byok;
          // Green when the key works, red for anything that needs attention
          const status = !byok.has_key
            ? null
            : byok.status === "invalid"
              ? { tone: "red", text: "Invalid" }
              : byok.status === "no_credits"
                ? { tone: "red", text: "No balance" }
                : { tone: "green", text: "Active" };
          return (
            <PlanCard
              label={
                <span className="plan-cards__label-info">
                  OpenRouter key
                  <Tooltip
                    placement="top"
                    title="Connect your own OpenRouter key and AI runs on your OpenRouter balance, with no Upwex credit limits. If the key stops working, AI uses your plan credits."
                  >
                    <HiOutlineInformationCircle size={15} className="plan-cards__info-icon" />
                  </Tooltip>
                </span>
              }
              value={
                byok.has_key ? (
                  <span className="plan-cards__key">****{byok.key_last4 ?? ""}</span>
                ) : (
                  "Not connected"
                )
              }
              subtitle={
                status ? (
                  <span className={`plan-cards__key-status plan-cards__key-status--${status.tone}`}>{status.text}</span>
                ) : (
                  "Connect to generate without credit limits"
                )
              }
              buttonText={byok.has_key ? "Update" : "Connect"}
              isLoading={isFetching}
              onClick={() => setShowKeyModal(true)}
            />
          );
        })()}
      </div>
      <ByokKeyModal open={showKeyModal} onClose={() => setShowKeyModal(false)} />

      <Modal
        open={showCancelModal}
        className="cancel-subscription-modal"
        closeIcon={<HiOutlineXMark size={20} />}
        onCancel={() => !isCanceling && setShowCancelModal(false)}
        footer={null}
        title={null}
        width={400}
      >
        <div className="cancel-modal">
          <div className="cancel-modal__icon">
            <HiOutlineExclamationTriangle size={28} />
          </div>
          <h3 className="cancel-modal__title">Cancel Subscription</h3>
          <p className="cancel-modal__desc">
            Your <strong>{currentPlan?.name}</strong> plan will remain active until the end of the current billing period. After that, your team will lose access to paid features.
          </p>
          <div className="cancel-modal__buttons">
            <FormButton
              theme="grey"
              onClick={() => setShowCancelModal(false)}
              disabled={isCanceling}
            >
              Keep Plan
            </FormButton>
            <FormButton
              theme="red"
              loading={isCanceling}
              disabled={isCanceling}
              onClick={handleCancelSubscription}
            >
              Cancel Plan
            </FormButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};
